import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useScrollAnimation, fadeInUpVariants, staggerContainerVariants } from '../hooks/useScrollAnimation';
import FullscreenPhoneMockup from './FullscreenPhoneMockup';

const HowItWorksContainer = styled(motion.div)`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #0A0118;
  color: #fff;
  overflow: hidden;
  min-height: 100vh;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, #6832DC 0%, #341A78 10%, black 100%, #000000 100%); 
  z-index: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  z-index: 1;
  padding: 0 20px;
  height: 100vh;
  position: relative;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    padding: 60px 15px;
  }
`;

const HeaderSection = styled.div`
  position: absolute;
  top: 8%;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 3;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    top: 5%;
  }
`;

const Title = styled(motion.h2)`
  font-size: 68px;
  font-family: "Black Han Sans", sans-serif;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  font-weight: 900;
  margin-bottom: 10px;
  color: white;
  position: relative;
  text-align: center;
  top:30px;
  letter-spacing: -0.02em;
  z-index: 1000900;
  text-shadow: 0 2px 20px rgba(104, 50, 220, 0.6);
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
  
  @media (max-width: 480px) {
    font-size: 34px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 24px;
  font-weight: 600;
  color: #DCB9FF;
  font-family: "Black Han Sans", sans-serif;
  font-weight: 400;
  font-style: normal;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
  margin-bottom: 30px;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const PhoneContainer = styled(motion.div)`
  position: relative;
  z-index: 2;
  margin-top: 100px;
  // margin-bottom: 100px;
  // transform: scale(0.4);
  @media (max-width: 768px) {
    margin-top: 50px;
  }
`;

const SideButton = styled.div`
  position: absolute;
  background: #0A0A0A;
  border-radius: 2px;
  z-index: 11;
  
  &.power {
    width: 4px;
    height: 60px;
    right: -0px;
    top: 140px;
    border-left: 1px solid #777;
  }
  
  &.volume-up {
    width: 4px;
    height: 40px;
    left: -0px;
    top: 130px;
    border-right: 1px solid #777;
  }
  
  &.volume-down {
    width: 4px;
    height: 40px;
    left: -0px;
    top: 185px;
    border-right: 1px solid #777;
  }
  
  &.silent-switch {
    width: 5px;
    height: 18px;
    left: -0px;
    top: 80px;
    border-right: 1px solid #777;
    border-radius: 2px;
  }
`;

const LightSpot = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(143, 0, 255, 0.15) 0%, rgba(143, 0, 255, 0) 70%);
  z-index: 0;
  
  &.top-left {
    top: -400px;
    left: -400px;
  }
  
  &.bottom-right {
    bottom: -400px;
    right: -400px;
  }
`;

const FloatingParticle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: ${props => props.color};
  opacity: ${props => props.opacity};
  z-index: 1;
  filter: blur(${props => props.blur}px);
  animation: float ${props => props.duration}s ease-in-out infinite;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) translateX(0);
    }
    50% {
      transform: translateY(${props => props.floatDistance}px) translateX(${props => props.floatDistance/2}px);
    }
  }
`;

const HowItWorks = () => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.error('Ошибка воспроизведения видео:', e));
    }
  }, []);
  
  const particles = [
    { size: 8, color: '#8000FF', opacity: 0.6, blur: 3, duration: 7, top: 20, left: 20, floatDistance: 20 },
    { size: 15, color: '#B69DFF', opacity: 0.4, blur: 4, duration: 9, top: 70, left: 80, floatDistance: -30 },
    { size: 10, color: '#AA52FF', opacity: 0.5, blur: 2, duration: 8, top: 30, left: 85, floatDistance: 25 },
    { size: 12, color: '#6A00D1', opacity: 0.3, blur: 5, duration: 6, top: 80, left: 15, floatDistance: -20 },
    { size: 6, color: '#8000FF', opacity: 0.7, blur: 1, duration: 10, top: 40, left: 40, floatDistance: 15 },
  ];

  return (
    <HowItWorksContainer>
      <BackgroundGradient />
      
      <LightSpot className="top-left" />
      <LightSpot className="bottom-right" />
      
      {particles.map((particle, index) => (
        <FloatingParticle key={index} {...particle} />
      ))}
      
      <ContentWrapper>
        <HeaderSection>
          <Title 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            How It Works?
          </Title>
        </HeaderSection>
        
        <PhoneContainer
          initial={{ opacity: 0, y: 50 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            x: [0, -5, 0, 5, 0],
            rotateZ: [0, -0.5, 0, 0.5, 0]
          }}
          transition={{ 
            y: {
              type: "spring",
              stiffness: 80,
              damping: 15,
              delay: 0.3
            },
            x: {
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse"
            },
            rotateZ: {
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }}
          whileHover={{ 
            y: -10,
            transition: { duration: 1.5, repeat: Infinity, repeatType: "reverse" }
          }}
        >
          <SideButton className="power" />
          <SideButton className="volume-up" />
          <SideButton className="volume-down" />
          <SideButton className="silent-switch" />
          
          <FullscreenPhoneMockup videoSrc={require('../assets/video3.mp4')} />
        </PhoneContainer>
      </ContentWrapper>
    </HowItWorksContainer>
  );
};

export default HowItWorks; 